import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';

// RxJS
import { auditTime, filter, map, Subject, switchMap, takeUntil } from 'rxjs';

// Services
import { RecordsService } from '@modules/records/services/records.service';
import { ProjectService } from '@modules/project/services/project.service';
import { UsersService } from '@modules/users/services/users.service';
import { AlertService } from '@modules/elements/services/alert.service';
import { NotificationService } from '@modules/elements/services/notification.service';

// Components
import { CreateRecordComponent } from '../create-record/create-record.component';

// Types
import { MatDialog } from '@angular/material/dialog';
import { Record } from '@modules/records/types/record';
import { MatTableDataSource } from '@angular/material/table';
import { Project } from '@modules/project/types/project';
import { UserPermissions } from '@modules/users/types/user';
import { Account } from '@modules/users/types/account';

@Component({
    selector: 'app-record-list',
    templateUrl: './record-list.component.html',
    styleUrl: './record-list.component.less',
    standalone: false
})
export class RecordListComponent implements OnInit, OnDestroy {

  // Public
  public records: Record[] = [];
  public recordDateInfo: {[key: string]: {displayDate: string | null, start: string | null, end: string | null | undefined}} = {};
  public filter: 'upcomming' | 'inprogress' | 'completed' | null = null;
  public dataSource: MatTableDataSource<Record> = new MatTableDataSource<Record>();
  public project?: Project;
  public permissions?: UserPermissions[];
  public account?: Account;

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private recordsService: RecordsService,
    private projectService: ProjectService,
    private usersService: UsersService,
    private alertService: AlertService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private router: Router,
    private datePipe: DatePipe,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Get Current Project
    this.projectService.getCurrentProject()
      .pipe(takeUntil(this.alive))
      .subscribe(project => {
        this.project = project;
        this.recordsService.requestRecords();
      });
    // Refresh the list when a record changes server-side
    this.recordsService.getRecordUpdated()
      .pipe(
        auditTime(1000),
        takeUntil(this.alive)
      )
      .subscribe(() => this.recordsService.requestRecords());
    // Get Records
    this.recordsService.getRecords()
      .pipe(
        map(records => {
          this.recordDateInfo = {};
          records.forEach(record => {
            const recordId = record.id!;
            this.recordDateInfo[recordId] = {displayDate: '', start: '', end: ''};
            // if (record.interval === 'once') {
              this.recordDateInfo[recordId].displayDate = this.datePipe.transform((record.startTime), 'mediumDate');
            // } else if (record.interval === 'repeat') {
            //   const rrule = this.rrulePipe.transform(record.rrule) as RRule;
            //   switch (rrule.origOptions?.freq) {
            //     case Frequency.MONTHLY:
            //       this.recordDateInfo[recordId].displayDate = 'Every month on the ' + this.datePipe.transform(this.rrulePipe.transform(record.rrule, 'date') as Date, 'dd');
            //       break;
            //     case Frequency.WEEKLY:
            //       this.recordDateInfo[recordId].displayDate = this.rrulePipe.transform(record.rrule, 'weekdays') as string;
            //       break;
            //     case Frequency.DAILY:
            //       this.recordDateInfo[recordId].displayDate = 'Everyday';
            //       break;
            //     default:
            //       break;
            //   }
            // }
            // this.recordDateInfo[recordId].end = record.durationValue;
            this.recordDateInfo[recordId].end = '';
            switch (record.durationType) {
              case 'seconds':
                // this.recordDateInfo[recordId].end = (+(record.durationValue || 0) / 60) + 'min';
                break;
              case 'frames':
                this.recordDateInfo[recordId].end += ' frames';
                break;
              case 'timecode':
                this.recordDateInfo[recordId].end = 'End TC: ' + (record as any).end;
                break;
            
              default:
                break;
            }
            if (this.project?.startTrigger) {
              this.recordDateInfo[recordId].start = this.datePipe.transform((record.startTime), 'shortTime');
              switch (this.project.startTrigger?.type) {
                case 'startTime':
                  break;
                case 'scteTrigger':
                  this.recordDateInfo[recordId].end += ' (SCTE Trigger)';
                  break;
                case 'timecode':
                  this.recordDateInfo[recordId].end += ' (Start TC: ' + this.project.startTrigger?.value + ')';
                  break;
                case 'manual':
                  this.recordDateInfo[recordId].end += ' (Manual)';
                  break;
              
                default:
                  break;
              }
            }
          });
          return records;
        }),
        map(records => records.sort((a: Record, b: Record) => new Date(a.startTime || 0).getTime() - new Date(b.startTime || 0).getTime())),
        takeUntil(this.alive)
      )
      .subscribe(records => {
        this.records = records;
        this.dataSource.data = this.records;
      });
    // Get Permissions
    this.usersService.getPermissions()
      .pipe(takeUntil(this.alive))
      .subscribe(permissions => this.permissions = permissions);
    // Get Account
    this.usersService.getAccount()
      .pipe(takeUntil(this.alive))
      .subscribe(account => this.account = account);

      // Filter Table
    this.dataSource.filterPredicate = (record: Record, filter: string) => {
      return !filter ||
        (filter === 'upcomming' && record.status === 'scheduled') ||
        (filter === 'inprogress' && (record.status === 'preparing' || record.status === 'inprogress') ) ||
        (filter === 'completed' && (record.status === 'finished' || record.status === 'canceled' || record.status === 'error'));
     };
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  createRecord(): void {
    this.dialog.open(CreateRecordComponent, {data: {}, disableClose: true});
  }

  cancelRecord(record: Record, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (record) {
      this.recordsService.cancelRecord(record);
    }
  }

  deleteRecord(record: Record, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (record?.id && this.permissions?.includes('record:delete')) {
      this.alertService.show(
        'Delete record',
        `Would you like to delete "${record.title}" record?`,
        ['Yes', 'No'],
        0
      )
        .pipe(
          filter(respose => respose === 0),
          switchMap(() => this.recordsService.deleteRecords([record.id!]))
        )
        .subscribe(response => {
          this.notificationService.success(`Record "${record.title}" deleted successfully`, '', true);
        });
    }
  }

  filterRecord(filter: 'upcomming' | 'inprogress' | 'completed' | null): void {
    this.filter = filter;
    this.dataSource.filter = this.filter as string;
  }

}
